/**
 * RealmSwitcher - Toggle zwischen GoodLife und GoodWork
 *
 * Ein dezenter Switch im Header, der zwischen den beiden Modi umschaltet.
 * GoodLife ist der Standard und wird prominent dargestellt.
 */

import React from 'react';
import { Briefcase, Leaf } from 'lucide-react';
import { useRealm } from '../../context/RealmContext';
import type { Realm } from '../../types';

interface RealmSwitcherProps {
  compact?: boolean;  // Kompakte Version fuer Mobile
  className?: string;
}

export function RealmSwitcher({ compact = false, className = '' }: RealmSwitcherProps) {
  const { realm, setRealm, isGoodWork } = useRealm();

  const handleChange = (newRealm: Realm) => {
    setRealm(newRealm);
  };

  if (compact) {
    // Kompakte Version: Nur Icon-Toggle
    return (
      <button
        onClick={() => handleChange(isGoodWork ? 'goodlife' : 'goodwork')}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
          isGoodWork
            ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
            : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
        } ${className}`}
        title={isGoodWork ? 'Zu GoodLife wechseln' : 'Zu GoodWork wechseln'}
      >
        {isGoodWork ? (
          <>
            <Briefcase className="w-4 h-4" />
            <span>Work</span>
          </>
        ) : (
          <>
            <Leaf className="w-4 h-4" />
            <span>Life</span>
          </>
        )}
      </button>
    );
  }

  // Volle Version: Segmented Control
  return (
    <div className={`flex items-center bg-gray-100 rounded-full p-1 ${className}`}>
      <button
        onClick={() => handleChange('goodlife')}
        className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
          realm === 'goodlife'
            ? 'bg-white text-emerald-700 shadow-sm'
            : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        <Leaf className="w-4 h-4" />
        <span>GoodLife</span>
      </button>
      <button
        onClick={() => handleChange('goodwork')}
        className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
          realm === 'goodwork'
            ? 'bg-white text-indigo-700 shadow-sm'
            : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        <Briefcase className="w-4 h-4" />
        <span>GoodWork</span>
      </button>
    </div>
  );
}

/**
 * RealmBadge - Kleines Badge zur Anzeige des aktuellen Realms
 */
export function RealmBadge({ realm, size = 'sm' }: { realm?: Realm; size?: 'sm' | 'md' }) {
  const displayRealm = realm || 'goodlife';
  const isWork = displayRealm === 'goodwork';

  const sizeClasses = size === 'sm' ? 'text-xs px-2 py-0.5' : 'text-sm px-3 py-1';

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-medium ${sizeClasses} ${
        isWork ? 'bg-indigo-100 text-indigo-700' : 'bg-emerald-100 text-emerald-700'
      }`}
    >
      {isWork ? <Briefcase className="w-3 h-3" /> : <Leaf className="w-3 h-3" />}
      {isWork ? 'Work' : 'Life'}
    </span>
  );
}

export default RealmSwitcher;
